import { CheckCircleOutline, Lock, Pause } from '@mui/icons-material';
import { Box, Theme, Typography, useTheme } from '@mui/material';

import { PlayArrowFilledIcon, PlayArrowOutlinedIcon } from '@/icons';
import { EpisodeStatus } from '@/types';
import { convertSecondsToHHMMSS } from '@/utils';

const HEIGHT = 26;
const PROGRESS_BAR_BUFFER = 5;

export type EpisodeToolbarComponentProps = {
  episodeStatus?: EpisodeStatus;
  onClick: (event: React.MouseEvent) => void;
  isTrailerActive: boolean;
  isEpisodePlaying: boolean;
  trailerDuration: number;
  episodeDuration: number;
  playerPosition: number;
  episodePlayedDuration: number;
  showPremium: boolean;
};

export const EpisodeToolbar = ({
  episodeStatus = 'none',
  onClick,
  isTrailerActive,
  isEpisodePlaying,
  trailerDuration,
  episodeDuration,
  playerPosition,
  episodePlayedDuration,
  showPremium,
}: EpisodeToolbarComponentProps) => {
  const theme = useTheme();
  const episodeDurationLeft = convertSecondsToHHMMSS(
    episodeDuration - episodePlayedDuration,
  );
  const trailerDurationLeft = convertSecondsToHHMMSS(
    trailerDuration - playerPosition,
  );

  const percentage = getPercentage({
    episodeStatus,
    playerPosition,
    episodePlayedDuration,
    trailerDuration,
    episodeDuration,
    showPremium,
  });
  const label = getLabel({
    episodeStatus,
    isTrailerActive,
    trailerDurationLeft,
    episodeDurationLeft,
    showPremium,
  });
  const textColor = getTextColor(episodeStatus);
  const background = getBackgroundColor({ episodeStatus, showPremium, theme });

  return (
    <Box sx={styles.root} onClick={onClick}>
      <Box
        sx={[
          styles.background,
          {
            width: `${percentage < 100 ? Math.min(100 - (percentage || 0) + PROGRESS_BAR_BUFFER, 100) : 0}%`,
          },
        ]}
      />
      <Box
        sx={[
          styles.progressBar,
          {
            width: `${percentage || 0}%`,
            ...background,
          },
        ]}
      />
      <Box sx={styles.barContents}>
        {getIcon({
          episodeStatus,
          isTrailerActive,
          isEpisodePlaying,
          showPremium,
        })}
        <Typography
          sx={{ color: textColor, textWrap: 'nowrap' }}
          variant="body3SemiBold">
          {label}
        </Typography>
      </Box>
      <Box sx={styles.hoverOverlay} />
    </Box>
  );
};

const getPercentage = ({
  episodeStatus,
  playerPosition,
  episodePlayedDuration,
  trailerDuration,
  episodeDuration,
  showPremium,
}: {
  episodeStatus: EpisodeStatus;
  playerPosition: number;
  episodePlayedDuration: number;
  trailerDuration: number;
  episodeDuration: number;
  showPremium: boolean;
}) => {
  if (episodeStatus === 'none') {
    if (showPremium) return 100;
    return trailerDuration ? (playerPosition / trailerDuration) * 100 : 0;
  }
  if (episodeStatus === 'onGoing') {
    return episodeDuration
      ? (episodePlayedDuration / episodeDuration) * 100
      : 0;
  }
  return 100;
};

const getIcon = ({
  episodeStatus,
  isTrailerActive,
  isEpisodePlaying,
  showPremium,
}: {
  episodeStatus: EpisodeStatus;
  isTrailerActive: boolean;
  isEpisodePlaying: boolean;
  showPremium: boolean;
}) => {
  const textColor = getTextColor(episodeStatus);
  const sx = [styles.icon, { color: textColor }];
  if (episodeStatus === 'none') {
    if (showPremium) return <Lock sx={sx} />;
    return isTrailerActive && isEpisodePlaying ? (
      <Pause sx={sx} />
    ) : (
      <PlayArrowOutlinedIcon sx={sx} />
    );
  }
  if (episodeStatus === 'onGoing') {
    return isEpisodePlaying ? (
      <Pause sx={sx} />
    ) : (
      <PlayArrowFilledIcon sx={sx} />
    );
  }

  if (episodeStatus === 'verified') return <CheckCircleOutline sx={sx} />;
};

const getLabel = ({
  episodeStatus,
  isTrailerActive,
  trailerDurationLeft,
  episodeDurationLeft,
  showPremium,
}: {
  episodeStatus: EpisodeStatus;
  isTrailerActive: boolean;
  trailerDurationLeft: string;
  episodeDurationLeft: string;
  showPremium: boolean;
}) => {
  if (episodeStatus === 'none') {
    if (showPremium) return 'Premium';
    return isTrailerActive ? `${trailerDurationLeft} left` : 'Preview';
  }
  if (episodeStatus === 'onGoing') {
    return `${episodeDurationLeft} left`;
  }
  if (episodeStatus === 'quizReady') return 'Quiz Ready';
  if (episodeStatus === 'assessmentReady') return 'Assessment';
  if (episodeStatus === 'verified') return 'Completed';
  return null;
};

const getTextColor = (episodeStatus: EpisodeStatus) => {
  return episodeStatus === 'none' || episodeStatus === 'onGoing'
    ? 'primary.main'
    : 'white';
};

const getBackgroundColor = ({
  episodeStatus,
  showPremium,
  theme,
}: {
  episodeStatus: EpisodeStatus;
  showPremium: boolean;
  theme: Theme;
}) => {
  if (episodeStatus === 'none') {
    if (showPremium) return { background: theme.palette.gradients.premium };
    return { backgroundColor: theme.palette.secondary.active };
  }
  if (episodeStatus === 'onGoing') {
    return { backgroundColor: theme.palette.secondary.active };
  }
  if (episodeStatus === 'quizReady' || episodeStatus === 'assessmentReady') {
    return { background: theme.palette.chapter.quizReady };
  }
  return { background: theme.palette.gradients.primary };
};

const styles = {
  root: {
    position: 'relative',
    display: 'flex',
    height: HEIGHT,
    borderRadius: HEIGHT / 2,
    flexDirection: 'row',
    overflow: 'hidden',
    px: 1.5,
    cursor: 'pointer',
    width: 'fit-content',
    zIndex: 0,
  },
  background: {
    backgroundColor: 'grey50',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
  },
  progressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
  },
  barContents: {
    display: 'flex',
    flexDirection: 'row',
    zIndex: 1,
    alignItems: 'center',
  },
  icon: {
    width: 14,
    height: 14,
    mr: 0.5,
  },
  hoverOverlay: {
    transition: 'opacity 0.15s',
    position: 'absolute',
    opacity: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: 2,
    backgroundColor: 'black',
    '&:hover': {
      opacity: 0.1,
    },
  },
};
